import { AfterViewInit, Component, DoCheck, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';
import { NavigationEnd, Router, RouterOutlet, ActivatedRoute } from '@angular/router';
import { fades } from "./routes/routes.animation";
import { SidebarService, AppService, SessionService, GoogleAnalyticsService, ApplicationInsightService, AuthService, SpeechRecognitionService, OAuthService, DebugLoggerService } from './services';

import * as _moment from "moment";
import { default as _rollupMoment, Moment } from "moment";
import "moment-timezone";
import { TranslateService } from '@ngx-translate/core';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { DatePickerLocale, ApplicationInsightModel, B2CTokenResponse } from './models';
import { AppStateHolder, TimeLine } from './shared';
import { UserProperty, CompanyProperty, AppProperty } from './properties';
import { apps } from './constants';
import { interval, Subscription, lastValueFrom, filter, map, mergeMap, Subject, takeUntil, fromEvent, combineLatest, merge, of, EMPTY, switchMap, startWith, catchError, distinctUntilChanged } from 'rxjs';
import { SwUpdate } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';

const moment = _rollupMoment || _moment;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [fades],
  encapsulation: ViewEncapsulation.None
})

export class AppComponent implements OnInit, DoCheck, OnDestroy {
  sidebarSrv = inject(SidebarService);
  sessionService = inject(SessionService);
  private _translator = inject(TranslateService);
  private _messageService = inject(MessageService);
  private _primeConfig = inject(PrimeNGConfig);
  appStateHolder = inject(AppStateHolder);
  private _appService = inject(AppService);
  userProperty = inject(UserProperty);
  private companyProperties = inject(CompanyProperty);
  private _router = inject(Router);
  private _timelime = inject(TimeLine);
  private activatedRoute = inject(ActivatedRoute);
  appProperty = inject(AppProperty);
  private swUpdate = inject(SwUpdate);
  private _appInsightsService = inject(ApplicationInsightService);
  speechRecognitionService = inject(SpeechRecognitionService);
  private _authService = inject(AuthService);
  private _oauthService = inject(OAuthService);
  private _debugLogger = inject(DebugLoggerService);


  @HostListener('window: resize', ['$event'])
  onresize(event) {
    this.innerWidth = window.innerWidth;
  }

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {

    this._translator.setDefaultLang('it');
    this._primeConfig.setTranslation(DatePickerLocale.it);

    this.swUpdate.checkForUpdate().then(event => {
      if (event)
        console.log('New version available. Reload the page to update.')
    });
  }

  impersonateLoader: boolean = false;
  innerWidth: any;

  subscription: Subscription;
  subscriptionImpersonate: Subscription;

  visibleSidebar: boolean = false;
  visibleAlertSidebar: boolean = false;
  visibleCopilotSidebar: boolean = false;
  visibleSupportSidebar: boolean = false;

  activity: any;

  alertsCount: any = 0;

  showAIIcon: boolean = false;

  userAuthProfile: ApplicationInsightModel;
  authProfileMapped: boolean = false;

  b2cAuthUrl: string = '';
  authLoading: boolean = false;

  idChat: string = '';
  amAssistantBotAppId: string = "23A331CD-85FD-4F72-A764-AAA3099CDF7A";
  bearerToken: string | null = null;
  showTryNewAttendanceSheetButton: boolean = false;
  private readonly tryNewAttendanceSheetAllowedEmailDomains: string[] = [
    '@neworg.net',
    '@cassoneassociati.it',
    '@studiocassone.it'
  ];
  private readonly tryNewAttendanceSheetUrl = 'https://amsuite-attendancesheet.neworg.net/lul/managelul';

  @ViewChild('oauthPanel') oauthPanel: OverlayPanel;
  selectedOAuthProvider: string = null;
  oauthAuthorizingProvider: string = null;
  oauthAutoRefreshSub: Subscription | null = null;
  impersonateAutoRefreshSub: Subscription | null = null;
  private lastImpersonateState: boolean | null = null;

  ngOnInit() {
    try {
      this._debugLogger.logLifecycle('AppComponent', 'ngOnInit');
      this.appProperty.loadingData = Promise.resolve(true);
      this.appProperty.setAppClass('app-orange');
      this.appProperty.setAppName('AmSuite');
      this.innerWidth = window.innerWidth;

      this.sidebarSrv.showSidebar = false;
      this.sidebarSrv.subscribeEventsNavigation();
      this._timelime.init();
    } catch (error) {
      this.appStateHolder.removeCookie('authType');
      this.appStateHolder.removeCookie('authToken');
      this.appStateHolder.removeCookie('authTypeDomain');
      this.appStateHolder.removeCookie('ImpersonateState')
      this._router.navigate(['/login']);
    }

    this.bearerToken = this.getBearerToken();

    lastValueFrom(this._appService.getRealUser()).then(
      async realUser => {
        this.userProperty.setRealUser(realUser);
        this.updateTryNewAttendanceSheetButtonVisibility();
        this._debugLogger.logAction('AppComponent', 'realUser loaded', realUser);
        this.getAssistantPermissions();

        let defaultLanguage = realUser.defaultLanguage === 'en' ? 'en-US' : 'it-IT';
        this.speechRecognitionService.updateLanguage(defaultLanguage);

        this._appService.impersonateActive().subscribe({
          next: s => {
            this.appStateHolder.removeCookie('ImpersonateState');

            this.appProperty.setIsImpersonateActive(s);
            this.updateTryNewAttendanceSheetButtonVisibility();
            this.appStateHolder.setCookie('ImpersonateState', s.toString());

            if (s) {
              this._appService.getUser().subscribe({
                next: currentUser => {
                  this.setUserParams(currentUser);
                }
              })
            } else {
              this.setUserParams(realUser);
            }

            const cookieDef = this.appStateHolder.getCookie('authType');

            if (this.userProperty.realUserModel.email && !cookieDef) {

              lastValueFrom(this._appService.getAuthType(this.userProperty.realUserModel.email)).then(
                authType => {
                  let aT = authType.toString() === 'asp' ? 'asp' : 'b2c';
                  this.appStateHolder.setCookie('authType', aT);

                  if (aT === 'b2c') {
                    this.appStateHolder.setCookie('authTypeDomain', authType.toString());

                    lastValueFrom(this._appService.getB2CAuthToken()).then((token: B2CTokenResponse) => {
                      this.appStateHolder.setCookie('authToken', token.access_token);
                      this.bearerToken = token.access_token;
                    }).catch(error => {
                      console.error('Error refreshing B2C auth token:', error);
                    });
                  }

                })
            } else if (cookieDef && cookieDef === 'b2c') {
              const currentDomain = this.appStateHolder.getCookie('authTypeDomain');

              if (!currentDomain && this.userProperty.realUserModel.email) {
                lastValueFrom(this._appService.getAuthType(this.userProperty.realUserModel.email)).then(
                  authType => {
                    this.appStateHolder.setCookie('authTypeDomain', authType.toString());
                  }).catch(error => {
                    console.error('Error fetching auth domain for B2C user:', error);
                  });
              }

              lastValueFrom(this._appService.getB2CAuthToken()).then((token: B2CTokenResponse) => {
                this.appStateHolder.setCookie('authToken', token.access_token);
                this.bearerToken = token.access_token;
              })
            }

            if (this.appStateHolder.getCookie('authType') === 'asp') {
              this.appStateHolder.removeCookie('authToken');
              this.appStateHolder.removeCookie('authTypeDomain');
              this.bearerToken = null;
            }

            this._appService.getDataOwner().then(async dataOwner => {
              this.userProperty.dataOwnerId = dataOwner;
              this.getOAuthConfiguration();
              // Start auto-refresh of OAuth providers after initial load
              this.initOAuthProvidersAutoRefresh();
              // Start auto-refresh of impersonation state after initial load
              this.lastImpersonateState = s;
              this.initImpersonateAutoRefresh();
              await lastValueFrom(this._appService.getUserCompanies()).then(result => {
                this.appStateHolder.mapDevURL(result)
                this.userProperty.setUserCompanies(result);
                let company: any;

                if (dataOwner)
                  company = this.userProperty.userCompaniesList.find(c => { return c.id.toLowerCase() === dataOwner.toLowerCase() });

                if (!company || !dataOwner)
                  company = this.userProperty.userCompaniesList[0];

                this.appProperty.loadingData = Promise.resolve(false);
                this.onSelectCompany(company);
              });
            })
          }
        })

        this._appService.getUserParty().subscribe(party => {
          this.userProperty.setUserParty(party);
        });

      }).catch(error => {
        if (error && error.status === 504) {
          this.appStateHolder.removeCookie('authType');
          this.appStateHolder.removeCookie('authToken');
          this.appStateHolder.removeCookie('authTypeDomain');
          this.appStateHolder.removeCookie('ImpersonateState')
          this._router.navigate(['/login']);
        }
      });
  }


  ngDoCheck() {
    if (this.checkB2CAuthParams()) {
      const tokenIsValid = this._authService.tokenIsValid();

      if (!tokenIsValid)
        this.mapAuthLoginModal();
      else if (this.appStateHolder.showAuthLoginModal)
        this.appStateHolder.showAuthLoginModal = false;

    }

    if (!this.authProfileMapped)
      this.mapAppInsightModel();
  }

  getAssistantPermissions(): void {
    this.showAIIcon = false;
    this._appService.getAppUserPermissions(this.amAssistantBotAppId).subscribe({
      next: (response) => {
        if (response && response.length > 0)
          this.showAIIcon = true;
        this._debugLogger.logAction('AppComponent', 'assistantPermissions', { showAIIcon: this.showAIIcon });
      },
      error: (err) => {
        console.error('Errore nel caricamento dei permessi:', err);
      }
    });
  }

  mapAppInsightModel() {
    if (!this.userProperty.realUserModel
      || !this.userProperty.realUserModel.userName
      || !this.userProperty.realUserModel.fullName
      || !this.userProperty.dataOwnerId
    )
      return;

    this.authProfileMapped = true;
    this.userAuthProfile = new ApplicationInsightModel();
    this.userAuthProfile.ImpersonateState = this.appStateHolder.getCookie('ImpersonateState');
    this.userAuthProfile.AuthType = this.appStateHolder.getCookie('authType');
    this.userAuthProfile.AuthDomain = this.appStateHolder.getCookie('authTypeDomain');
    this.userAuthProfile.UserName = this.userProperty.realUserModel.userName;
    this.userAuthProfile.UserFullName = this.userProperty.realUserModel.fullName;
    this.userAuthProfile.DataOwnerId = this.userProperty.dataOwnerId;

    this._appInsightsService.setUserAuthProfile(this.userAuthProfile)

    this.subscribeRouterEvents()

  }

  mapAuthLoginModal() {
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {

      if (event.urlAfterRedirects === '/login') {
        this.appStateHolder.showAuthLoginModal = false;
        return;
      }
    });

    let isLoginPage = window.location.pathname === '/login';

    if (isLoginPage && this.appStateHolder.showAuthLoginModal) {
      this.appStateHolder.showAuthLoginModal = false;
      return;
    }

    if (this.appStateHolder.showAuthLoginModal)
      return;

    this.b2cAuthUrl = `${window.location.origin}/login`;
    this.appStateHolder.removeCookie('authTypeDomain');
    this.appStateHolder.removeCookie('authToken');
    this.appStateHolder.showAuthLoginModal = true;
  }

  checkB2CAuthParams(): boolean {
    const cookieDef = this.appStateHolder.getCookie('authType') ?? null;

    if (cookieDef && cookieDef === 'b2c' && this.userProperty.realUserModel)
      return true;

    return false;
  }

  setUserParams(user: any) {
    this._translator.setDefaultLang(user.defaultLanguage);
    this.setPrimeConfigSettings(user.defaultLanguage);
    this.sidebarSrv.setExpand(user.menuState);
    this.sessionService.setSession(user);
    this._debugLogger.logAction('AppComponent', 'setUserParams', { userName: user.userName, language: user.defaultLanguage });
  }

  openAuthLoginWindow() {
    this.authLoading = true;
    this.appStateHolder.removeCookie('authToken');

    const loginUrl = this.b2cAuthUrl || `${window.location.origin}/login`;

    if (Capacitor.isNativePlatform()) {
      sessionStorage.setItem('amsuite.auth.returnUrl', window.location.href);
      window.location.assign(loginUrl);
      return;
    }

    const loginWindow = window.open(loginUrl, '_blank', 'location=yes,height=650,width=650,scrollbars=yes,status=yes');

    if (!loginWindow) {
      window.location.assign(loginUrl);
      return;
    }

    const intervalId = setInterval(() => {
      if (loginWindow.closed) {
        clearInterval(intervalId);

        this.appStateHolder.showAuthLoginModal = false;
        this.authLoading = false;
        return;
      }

      try {
        if (loginWindow.location.href.includes('dashboard') && this._authService.tokenIsValid()) {
          clearInterval(intervalId);
          loginWindow.close();
          this.appStateHolder.showAuthLoginModal = false;
          this.authLoading = false;
        }
      } catch (e) {
        // Cross-origin navigation is expected during auth, ignore.
      }
    }, 500);
  }

  subscribeRouterEvents() {
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        mergeMap(route => route.data)
      )
      .subscribe(event => {
        const title = event['title'];
        if (title) {
          let pageName = 'AmSuite - ' + title;
          this._appInsightsService.trackPageView(pageName);
        }
      });
  }


  //#region App


  /**
   * Sets the selected company and performs necessary actions.
   * @param c The company object to be selected.
   */
  onSelectCompany(c): void {
    this._debugLogger.logAction('AppComponent', 'onSelectCompany', { id: c.id, name: c.name });
    this._appService.setDataOwner(c.id).then(() => {
      this.companyProperties.setCompanyApps(c.apps);
      this.userProperty.setSelectedCompany(c);
      this.appProperty.dataLoaded = Promise.resolve(true);

      // Deep-link preservation for /comunica routes on first arrival (e.g., email link).
      // sessionStorage persists across F5 but is empty in a new tab,
      // so we can distinguish "first arrival from email" from "F5 reload".
      const currentUrl = this._router.url;
      const isFirstArrival = !sessionStorage.getItem('amsuiteBootstrapped');
      sessionStorage.setItem('amsuiteBootstrapped', 'true');

      if (isFirstArrival && currentUrl.startsWith('/comunica')) {
        this.appProperty.loginPageNotVisible = Promise.resolve(true);
        return;
      }

      this.onSelectApp({ url: "/" });
    });
  }


  /**
   * Handles the selection of an app.
   * @param app The selected app object.
   * @returns void
   */
  onSelectApp(app): void {
    let appClass = app.cssParams ? app.cssParams.appClass : 'app-orange';
    let appName = app.functionName && app.functionName != 'AmSuite' ? 'AmSuite ' + app.functionName : 'AmSuite';
    this._debugLogger.logAction('AppComponent', 'onSelectApp', { id: app.id, url: app.url, appClass });

    this.appProperty.setAppClass(appClass);
    this.appProperty.setAppName(appName);

    if (app.url === "/") {
      this._router.navigate(['/']);
      return;
    }

    this._router.navigate(['app', app.id]);
  }


  /**
   * Changes the selected company and updates the data owner.
   * @param {any} company - The company object to be selected.
   * @returns {Promise<void>} - A promise that resolves when the data owner is updated.
   */
  changeCompany(company) {
    this._debugLogger.logAction('AppComponent', 'changeCompany', { id: company.id, name: company.name });
    this._appService.setDataOwner(company.id).then(() => {
      this.companyProperties.setCompanyApps(company.apps);

      this.userProperty.setSelectedCompany(company);
      window.location.href = '/dashboard';
    });
  }

  //#endregion

  //#region Alert

  showNotify() {
    console.log("New notify ready for toast")
  }


  /**
   * Updates the alerts count and performs additional actions.
   * @param event - The event object containing the updated alerts count.
   */
  updateAlertsCount(event: any) {
    this.alertsCount = event;
    var element = <HTMLInputElement>document.getElementById("myButton");

    if (element) {
      element.disabled = false;
      element.click();
      element.disabled = true;
    }
  }


  //#endregion

  //#region Activity


  /**
   * Determines whether the create activity feature is enabled.
   *
   * @returns {boolean} True if the create activity feature is enabled, false otherwise.
   */
  enableCreateActivity(): boolean {
    if (this.companyProperties.userCompanyApps) {
      let workflow = this.companyProperties.userCompanyApps.find(app => { return app.id.toLowerCase() == apps.calendar.toLowerCase() })
      if (workflow != undefined)
        return true;
      else
        return false;
    }
    return false;
  }


  /**
   * Creates an activity.
   */
  createActivity() {
    this._debugLogger.logAction('AppComponent', 'createActivity');
    this._appService.createActivity().subscribe({
      next: act => {
        this.activity = act;
        this.activity.isNew = true;
        let d = this._timelime.day.date;

        d.setHours(d.getHours() - d.getTimezoneOffset() / 60);
        this.activity.date = this.activity.expiryDate = d.toISOString();
        this._debugLogger.logAction('AppComponent', 'activity created', this.activity);
      }
    });
  }


  /**
   * Handles the saving of an activity based on the specified option.
   *
   * @param option - The option indicating the action to be taken.
   *                1 - Clear the activity.
   *                2 - Create a new activity.
   *                3 - Navigate to the activity page.
   */
  onActivitySaved(option: number) {
    this._debugLogger.logAction('AppComponent', 'onActivitySaved', { option });
    if (option == 1) {
      this.activity = null;
      return
    }
    if (option == 2) {
      this.createActivity();
      return;
    }
    if (option == 3) {
      this.activity = null;

      let url = this.appStateHolder.getAppUrl(apps.calendar);
      this.appStateHolder.setAppNavbarClass(apps.calendar)

      if (url.length === 0) {
        let link = ['/activity', this.appStateHolder.activityId];
        this._router.navigate(link)
        return;
      }

      let link = ['/app', `${url}/activity/${this.appStateHolder.activityId}`];
      this._router.navigate(link);
    }
  }

  onEditorClosed(event: any) {
    if (event)
      this.activity = null;
  }

  //#endregion

  //#region Impersonate
  // Impersonation state is seeded on init and kept in sync via initImpersonateAutoRefresh()
  //#endregion

  /**
   * Sets the PrimeNG configuration settings based on the specified language.
   * @param language The language code ('en' for English, 'it' for Italian).
   */
  setPrimeConfigSettings(language: any) {
    this._primeConfig.ripple = true;

    switch (language) {
      case 'en':
        this._primeConfig.setTranslation(DatePickerLocale.en);
        break;
      default:
        this._primeConfig.setTranslation(DatePickerLocale.it);
        break;
    }
  }

  /**
   * Removes the impersonation and reloads the page.
   */
  removeImpersonate() {
    this._debugLogger.logAction('AppComponent', 'removeImpersonate');
    this.impersonateLoader = true;
    this._appService.removeImpersonate().subscribe({
      complete: () => {
        this.impersonateLoader = false;
        this.userProperty.dataOwnerId = undefined;
        window.location.reload();
      }
    })
  }

  openTryNewAttendanceSheet(): void {
    this._debugLogger.logAction('AppComponent', 'openTryNewAttendanceSheet');
    this.appStateHolder.setAppNavbarClass(apps.attendanceSheet);
    this._router.navigate(['app', this.tryNewAttendanceSheetUrl]);
  }

  private updateTryNewAttendanceSheetButtonVisibility(): void {
    const realUser = this.userProperty.realUserModel;
    const rawEmail = realUser?.email ?? realUser?.Email ?? realUser?.userName ?? realUser?.UserName ?? '';
    const normalizedEmail = rawEmail.toString().trim().toLowerCase();
    const impersonateIsActive = !!this.appProperty.impersonateIsActive;
    const emailAllowed = this.tryNewAttendanceSheetAllowedEmailDomains.some(domain => normalizedEmail.includes(domain));

    this.showTryNewAttendanceSheetButton = impersonateIsActive
      && emailAllowed;
  }

  getSidebarClass() {
    if (this.innerWidth <= 700)
      return 'w-12'
    else if (this.innerWidth <= 1000)
      return 'w-10'
    else if (this.innerWidth <= 1200)
      return 'w-9'
    else if (this.innerWidth <= 1400)
      return 'w-8'
    else if (this.innerWidth <= 1500)
      return 'w-7'
    else
      return 'w-5'
  }

  /**
   * Load OAuth configuration and status for all providers
   */
  private getOAuthConfiguration(): void {
    this._oauthService.getProviders().subscribe({
      next: (providers) => {
        // Filter only active providers
        const activeProviders = providers.filter(provider => provider.isActive);
        this.appStateHolder.oauthState.providers = activeProviders;
        this._debugLogger.logAction('AppComponent', 'OAuth providers loaded', activeProviders);
      },
      error: (error) => {
        console.error('Error loading OAuth providers:', error);
      }
    });
  }

  /**
   * Show OAuth status panel
   */
  showOAuthPanel(event: any, provider: string): void {
    this.selectedOAuthProvider = provider;
    this.oauthPanel.toggle(event);
  }

  /**
   * Get OAuth provider status (active, expiring, expired)
   */
  getOAuthProviderStatus(providerName: string): 'active' | 'expiring' | 'expired' {
    const provider = this.appStateHolder.oauthState.providers.find(p => p.provider === providerName);

    if (!provider || !provider.hasValidToken) {
      return 'expired';
    }

    if (provider.refreshTokenExpiresAt) {
      const userTimezone = this.sessionService.getTimezone || moment.tz.guess();
      const expiresAt = moment.utc(provider.refreshTokenExpiresAt).tz(userTimezone);
      const now = moment.tz(userTimezone);
      const hoursUntilExpiry = expiresAt.diff(now, 'hours');

      if (hoursUntilExpiry <= 0) {
        return 'expired';
      } else if (hoursUntilExpiry <= 24) {
        return 'expiring';
      }
    }

    return 'active';
  }

  /**
   * Format OAuth expiry date
   */
  formatOAuthExpiryDate(providerName: string): string {
    const provider = this.appStateHolder.oauthState.providers.find(p => p.provider === providerName);
    if (provider?.refreshTokenExpiresAt) {
      const userTimezone = this.sessionService.getTimezone || moment.tz.guess();
      return moment.utc(provider.refreshTokenExpiresAt).tz(userTimezone).format('DD/MM/YYYY');
    }
    return '';
  }

  /**
   * Format OAuth expiry time
   */
  formatOAuthExpiryTime(providerName: string): string {
    const provider = this.appStateHolder.oauthState.providers.find(p => p.provider === providerName);
    if (provider?.refreshTokenExpiresAt) {
      const userTimezone = this.sessionService.getTimezone || moment.tz.guess();
      return moment.utc(provider.refreshTokenExpiresAt).tz(userTimezone).format('HH:mm');
    }
    return '';
  }

  /**
   * Authorize OAuth provider
   */
  authorizeOAuthProvider(provider: string): void {
    this._debugLogger.logAction('AppComponent', 'authorizeOAuthProvider', { provider });
    this.oauthAuthorizingProvider = provider;

    this._oauthService.authorize(provider).subscribe({
      next: (response) => {
        if (response.authorizationUrl) {
          // Open authorization URL in popup window
          const authWindow = window.open(
            response.authorizationUrl,
            '_blank',
            'location=yes,height=650,width=650,scrollbars=yes,status=yes'
          );

          if (authWindow) {
            // Monitor the popup window
            const checkInterval = setInterval(() => {
              try {
                // Check if window is closed
                if (authWindow.closed) {
                  clearInterval(checkInterval);
                  this.oauthAuthorizingProvider = null;
                  // Refresh OAuth providers after window closes
                  this.refreshOAuthProviders();
                  return;
                }

                // Try to access the URL (will fail for cross-origin)
                const currentUrl = authWindow.location.href;

                // Check if we're back on our domain (successful callback)
                if (currentUrl.includes(window.location.hostname)) {
                  authWindow.close();
                  clearInterval(checkInterval);
                  this.oauthAuthorizingProvider = null;
                  // Refresh OAuth providers after successful auth
                  this.refreshOAuthProviders();
                }
              } catch (e) {
                // Cross-origin error is expected, continue monitoring
              }
            }, 500);

            // Cleanup interval after 10 minutes (timeout)
            setTimeout(() => {
              clearInterval(checkInterval);
              this.oauthAuthorizingProvider = null;
            }, 1200000);
          }
        }
      },
      error: (error) => {
        console.error(`Error authorizing ${provider}:`, error);
        this.oauthAuthorizingProvider = null;
      }
    });
  }

  /**
   * Refresh OAuth providers list
   */
  private refreshOAuthProviders(): void {
    this._oauthService.getProviders().subscribe({
      next: (providers) => {
        const activeProviders = providers.filter(provider => provider.isActive);
        this.appStateHolder.oauthState.providers = activeProviders;
      },
      error: (error) => {
        console.error('Error refreshing OAuth providers:', error);
      }
    });
  }

  /**
   * Initialize RxJS-based auto refresh for OAuth providers.
   * - Foreground: refresh every 60s
   * - Background: refresh every 5m (subject to browser throttling)
   * - Immediate refresh on focus/visible/online
   */
  private initOAuthProvidersAutoRefresh(): void {
    if (this.oauthAutoRefreshSub) return; // already running

    const visible$ = fromEvent(document, 'visibilitychange').pipe(
      map(() => !document.hidden),
      startWith(!document.hidden),
      distinctUntilChanged()
    );

    const focus$ = fromEvent(window, 'focus');

    const online$ = merge(
      of(navigator.onLine),
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).pipe(distinctUntilChanged());

    const periodic$ = combineLatest([visible$, online$]).pipe(
      switchMap(([visible, online]) => online
        ? interval(visible ? 120000 : 300000)
        : EMPTY
      )
    );

    const tick$ = merge(
      of(0), // initial immediate refresh
      focus$,
      visible$.pipe(filter(v => v)), // when becomes visible
      online$.pipe(filter(o => o)), // when back online
      periodic$
    );

    this.oauthAutoRefreshSub = tick$.pipe(
      switchMap(() => this._oauthService.getProviders()),
      map(providers => providers.filter(p => p.isActive)),
      catchError(err => {
        console.error('OAuth auto-refresh error:', err);
        return EMPTY;
      })
    ).subscribe(activeProviders => {
      this.appStateHolder.oauthState.providers = activeProviders;
    });
  }

  /**
   * Initialize RxJS-based auto refresh for Impersonation status.
   * - Foreground: refresh every 60s
   * - Background: refresh every 5m (subject to browser throttling)
   * - Immediate refresh on focus/visible/online
   */
  private initImpersonateAutoRefresh(): void {
    if (this.impersonateAutoRefreshSub) return; // already running

    const visible$ = fromEvent(document, 'visibilitychange').pipe(
      map(() => !document.hidden),
      startWith(!document.hidden),
      distinctUntilChanged()
    );

    const focus$ = fromEvent(window, 'focus');

    const online$ = merge(
      of(navigator.onLine),
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).pipe(distinctUntilChanged());

    const periodic$ = combineLatest([visible$, online$]).pipe(
      switchMap(([visible, online]) => online
        ? interval(visible ? 120000 : 300000)
        : EMPTY
      )
    );

    const tick$ = merge(
      focus$,
      visible$.pipe(filter(v => v)),
      online$.pipe(filter(o => o)),
      periodic$
    );

    this.impersonateAutoRefreshSub = tick$.pipe(
      switchMap(() => this._appService.impersonateActive()),
      catchError(err => {
        console.error('Impersonation auto-refresh error:', err);
        return EMPTY;
      })
    ).subscribe((s: boolean) => {
      // Update holder + cookie
      this.appProperty.setIsImpersonateActive(s);
      this.updateTryNewAttendanceSheetButtonVisibility();
      this.appStateHolder.removeCookie('ImpersonateState');
      this.appStateHolder.setCookie('ImpersonateState', s?.toString?.() ?? 'false');

      // Update user session only when state actually changes
      if (this.lastImpersonateState !== s) {
        this._debugLogger.logState('AppComponent', 'impersonateStateChanged', { from: this.lastImpersonateState, to: s });
        this.lastImpersonateState = s;
        if (s) {
          this._appService.getUser().subscribe({
            next: currentUser => this.setUserParams(currentUser)
          });
        } else {
          lastValueFrom(this._appService.getRealUser()).then(realUser => this.setUserParams(realUser));
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.oauthAutoRefreshSub) {
      this.oauthAutoRefreshSub.unsubscribe();
      this.oauthAutoRefreshSub = null;
    }
    if (this.impersonateAutoRefreshSub) {
      this.impersonateAutoRefreshSub.unsubscribe();
      this.impersonateAutoRefreshSub = null;
    }
  }

  /**
   * Retrieves the bearer token from cookies.
   * @returns The authentication token or null if not found.
   */
  private getBearerToken(): string | null {
    return this.appStateHolder.getCookie('authToken');
  }


  public prepareOutlet(outlet: RouterOutlet): RouterOutlet {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }
}
